import { jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import Form_module from "./Form.module.js";
import { FormContextProvider } from "./FormContext.js";
import { InternalStack } from "../stack/internal/InternalStack.js";
import { FieldSet } from "./internal/fieldSet/FieldSet.js";
import { Field } from "./internal/Field.js";
import { Label } from "./internal/Label.js";
import { HelpMessage } from "./internal/HelpMessage.js";
import { ValidationMessage } from "./internal/ValidationMessage.js";
import { createComponentNamespace } from "../../utilities/createComponentNamespace.js";
import { combineClasses } from "../../utilities/combineClasses.js";
import { useCombinedRefs } from "../../hooks/useCombinedRefs.js";
const FormComponent = /*#__PURE__*/ forwardRef(({ disabled, readOnly, required, size, status, onSubmit, className, gap = 'auto', ...props }, ref)=>{
    const [submitCounter, setSubmitCounter] = useState(0);
    const formRef = useRef(null);
    const contextValue = useMemo(()=>({
            disabled,
            readOnly,
            required,
            size,
            status,
            submitCounter
        }), [
        disabled,
        readOnly,
        required,
        size,
        status,
        submitCounter
    ]);
    useEffect(()=>{
        const firstInvalidElement = formRef.current?.querySelectorAll('[aria-invalid="true"]')[0];
        if (!firstInvalidElement) return;
        const focusInvalidElement = ()=>{
            firstInvalidElement.focus();
            firstInvalidElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        };
        const fieldsetAncestor = firstInvalidElement.closest('fieldset');
        if (fieldsetAncestor && 'true' === fieldsetAncestor.dataset.collapsed) {
            const observer = new MutationObserver(()=>{
                if ('false' === fieldsetAncestor.dataset.collapsed) {
                    observer.disconnect();
                    focusInvalidElement();
                }
            });
            observer.observe(fieldsetAncestor, {
                attributes: true,
                attributeFilter: [
                    'data-collapsed'
                ]
            });
            return ()=>{
                observer.disconnect();
            };
        }
        focusInvalidElement();
    }, [
        submitCounter
    ]);
    return /*#__PURE__*/ jsx(FormContextProvider, {
        value: contextValue,
        children: /*#__PURE__*/ jsx(InternalStack, {
            as: "form",
            noValidate: true,
            gap: 'auto' === gap ? '0' : gap,
            className: combineClasses(className, Form_module.form, 'auto' === gap && Form_module.autoLayout),
            ref: useCombinedRefs(formRef, ref),
            onSubmit: (event)=>{
                event.preventDefault();
                Promise.resolve(onSubmit(event)).finally(()=>{
                    setSubmitCounter((counter)=>counter + 1);
                });
            },
            ...props
        })
    });
});
const Form = createComponentNamespace({
    component: FormComponent,
    componentList: {
        FieldSet: FieldSet,
        Field: Field,
        Label: Label,
        HelpMessage: HelpMessage,
        ValidationMessage: ValidationMessage
    },
    namespace: 'Form'
});
Form.displayName = 'Form';
export { Form };
